<template>
  <div class="credentials-wrapper" :style="`backgroundColor: ${background}`">
    <BackgroundOverlay v-if="!(isCredentialsLoaded && isAuthCredentialLoaded) && isNACCMerchant" />
    <CenterLoader v-if="!(isCredentialsLoaded && isAuthCredentialLoaded) && !isNACCMerchant" />
    <div v-else class="app-container" :style="{ position: 'relative'}">
      <div class="app-header">
        <sgds-masthead />
        <AppBanner v-if="isHome" />
        <CheckoutHeader v-if="isCheckout && !isOtp" />
        <CartHeader v-else-if="isCart"/>
        <AppAppBar :background="background" v-else-if="!isOtp && !isRedemptionGroup" />
      </div>
      <div class="app-content">
        <slot></slot>
      </div>
      <AppFooter class="app-footer"/>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Options from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import '@govtechsg/sgds-web-component/themes/day.css'
import '@govtechsg/sgds-web-component/components/Masthead'


import CenterLoader from '@/components/CenterLoader.vue'
import AppAppBar from '@/components/AppAppBar.vue'
import AppBanner from '@/components/AppBanner.vue'
import AppFooter from '@/components/AppFooter.vue'
import CheckoutHeader from '@/components/CheckoutHeader.vue'
import BackgroundOverlay from '@/components/BackgroundOverlay.vue'
import CartHeader from '@/components/CartHeader.vue'

import { mapGetters } from 'vuex'
import { 
  AUTH_DISPATCH_TOKEN_NACCC, 
  AUTH_SET_CREDENTIAL, 
  SET_GOOGLE_TAG_MANAGER_IDS_IS_LOADED,
  AUTH_DISPATCH_GUEST_LOGIN,
} from '@/store/types'
import { loadScript } from '@gtm-support/core'
import { API_URL_BOT_PROCESS, CAPTCHA_V3_KEY, SCRIPT_NACCC } from '@/config/constants'
import { randomStr } from '@/utils/util.random'
import axios from 'axios'
import { load } from 'recaptcha-v3'
import { IReCaptchaInstance } from 'recaptcha-v3/dist/grecaptcha/grecaptcha'

interface IResUserRegister {
  data: {
    customerId: string;
    merchantId: string;
    token: string;
    custom_attributes: Record<string, unknown>;
    companyName: string;
  };
}

@Options({
  components: {
    CenterLoader,
    AppAppBar,
    AppBanner,
    AppFooter,
    CheckoutHeader,
    BackgroundOverlay,
    CartHeader,
  },
  computed: {
    ...mapGetters([
      'isCredentialsLoaded',
      'naccc_token',
      'apiKey',
      'googleTagManagerIds',
      'isGoogleTagManagerIdsLoaded',
      'isNACCMerchant',
      'customer',
      'isCulturePassEnable',
      'isGuestLogin',
      'merchant',
    ]),
  },
   props: {
    background: {
      type: String,
      default: '#FFFFFF',
    },
  },
})
export default class CredentialsLoader extends Vue {
  nacccIntervalId = 0
  nacccIntervalTime = 60000
  isAuthCredentialLoaded = false
  apiKey!: string
  isCredentialsLoaded!: boolean
  naccc_token!: string
  googleTagManagerIds!: string[]
  isGoogleTagManagerIdsLoaded!: boolean
  isNACCMerchant!: boolean
  customer!: string
  isCulturePassEnable!: boolean
  isGuestLogin!: boolean
  merchant!: string

  @Watch('apiKey')
  onApiKey(newValue: string): void {
    if (newValue) {
      this.$store.dispatch('loadCredentials')
    }
  }

  @Watch('googleTagManagerIds')
  onGoogleTagManagerIds(): void {
    const isGoogleTagIds = this.googleTagManagerIds && this.googleTagManagerIds.length > 0
    if( isGoogleTagIds && !this.isGoogleTagManagerIdsLoaded) {
      this.googleTagManagerIds.forEach((googleTagManagerId: string) => {
        loadScript(googleTagManagerId, {
          defer: false,
          compatibility: false,
        })  
      })
      this.$store.commit(SET_GOOGLE_TAG_MANAGER_IDS_IS_LOADED)
    }
  }

  async mounted(): Promise<void> {
    // TODO: Set language and merchant from api
    // What happens when both are undefined?
    if (this.apiKey && !this.isCredentialsLoaded) {
      await this.$store.dispatch('loadCredentials')
    }

    if (this.$store.getters.apiKey) {
      await this.loadAuthCredential(
        this.$store.getters.apiKey
      )
    } else {
      const apiKey = localStorage.getItem('apiKey')
      if (apiKey) {
        this.$store.commit('setAPIKey', { apiKey })
        await this.loadAuthCredential(apiKey)
      } else {
        parent.postMessage({ type: 'apikey_request' }, '*')
        window.addEventListener('message', (e) => {
          if (e.data.type === 'apikey_response') {
            this.$store.commit('setAPIKey', { apiKey: e.data?.payload })
            return this.loadAuthCredential(e.data?.payload)
          }
          return
        })
      }
    }

    //For NACC TOKEN
    if (this.naccc_token && !this.isGuestLogin) {
      clearInterval(this.nacccIntervalId)
    
      this.nacccIntervalId = setInterval(async () => {
        const status = await this.$store.dispatch(AUTH_DISPATCH_TOKEN_NACCC)

        if(!status) {
          clearInterval(this.nacccIntervalId)
        }
      }, 
        this.nacccIntervalTime
      )
    }

    if (this.isNACCMerchant && SCRIPT_NACCC) {
      const script = document.createElement('script')
      script.src = SCRIPT_NACCC
      document.head.append(script)
    } 
  }

  get isCheckout(): boolean {
    return this.$route.path.split('/').includes('checkout')
  }

  get isCart(): boolean {
    return this.$route.path.split('/').includes('cart')
  }

  get isOtp(): boolean {
    return this.$route.path.split('/').includes('otp')
  }

  get isRedemptionGroup(): boolean {
    return this.$route.name === 'RedemptionGroup'
  }

  get isHome(): boolean {
    return this.$route.name === 'Home'
  }

  beforeDestroy(): void {
    if (this.naccc_token) {
      clearInterval(this.nacccIntervalId)
    }
  }

  async loadAuthCredential(apiKey: string): Promise<void> {
    let merchantId = this.$route.params['merchant'] ?? this.merchant
    let customerId = localStorage.getItem(`vc_${merchantId}_attraction_ticket_customerid`) ?? this.customer

    if (!merchantId || !customerId) {
      const result = await this.usersRegister(apiKey)
      merchantId = result.merchantId
      customerId = result.customerId
    }
    this.isAuthCredentialLoaded = true

    this.$store.commit(AUTH_SET_CREDENTIAL, {
      merchant: merchantId,
      customerId: customerId,
    })

    if (this.isCulturePassEnable && !this.isGuestLogin) {
      try {
        await this.$store.dispatch(AUTH_DISPATCH_GUEST_LOGIN)
      } catch (error) {
        console.warn('Guest login failed:', error)
      }
    }
  }

  async usersRegister(apiKey: string): Promise<IResUserRegister['data']> {
    let username = localStorage.getItem(apiKey+'___username')
    let password = localStorage.getItem(apiKey+'___password')
    if (!username || !password) {
      username = randomStr(20)
      password = randomStr(20)
      localStorage.setItem(apiKey+'___username', username)
      localStorage.setItem(apiKey+'___password', password)
    }

    const recaptcha = await load(CAPTCHA_V3_KEY, {
      useRecaptchaNet: true,
      autoHideBadge: true,
    })
    const recaptchaToken = await recaptcha.execute('register')

    const res = await axios.post<IResUserRegister>(
      `${API_URL_BOT_PROCESS}/users/register`,
      JSON.stringify({
        userid: username,
        password: password,
        attributes: {},
        recaptchaToken
      }),
      {
        headers: {
          'Content-Type': 'application/json',
          'api-key': apiKey
        },
      }
    )

    delete (window as { grecaptcha?: IReCaptchaInstance }).grecaptcha

    return res.data.data
  }
}
</script>

<style lang="scss">
.credentials-wrapper {
  min-height: 100vh;
  height: 100%;
}

/* App layout using flexbox for auto-adjusting footer */
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  flex-shrink: 0;
}

.app-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Allows content to shrink if needed */
  padding-bottom: 2rem; /* Add space before footer */
}

/* Override child component height calculations that conflict with flexbox */
.app-content .home {
  height: 100% !important;
  min-height: auto !important;
}

.app-content .home-container {
  height: 100%;
}

.app-footer {
  flex-shrink: 0;
  margin-top: auto;
}
</style>
